import {
  Box,
  Flex,
  Heading,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Button } from '@app/components/chakra/snippets/button';
import { CaseTypeMetadataEnum } from '@app/types/create-case';

import QueueList from './queue-list';
import { useQueueListQuery, useQueueSelfAssign } from '../hooks';
import { QueueCase } from '../types';

// NOTE: This component is not set to be used on mobile for the time being
const QueueListContainer = (
  { isServiceQueueOpen, setIsServiceQueueOpen }: {
    isServiceQueueOpen: boolean;
    setIsServiceQueueOpen: (isServiceQueueOpen: boolean) => void;
  },
) => {
  const INITIAL_LIMIT = 24;
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  const [hasMore, setHasMore] = useState(false);
  const caseTypeIds = [CaseTypeMetadataEnum.SERVICE];

  const { data: queueList, isLoading } = useQueueListQuery({
    caseTypeIds,
    limit,
    enabled: Boolean(isServiceQueueOpen),
    setHasMore,
  });

  const loadMoreData = useCallback(() => {
    if (hasMore && !isLoading) {
      setLimit((prev) => prev + INITIAL_LIMIT);
    }
  }, [hasMore, isLoading]);

  const formatWaitTime = useCallback((createdAt: string) => {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60));
    return diffInMinutes;
  }, []);

  const jobsWithWaitTime = useMemo(() => {
    if (!queueList) return [];
    return queueList.map((job: QueueCase) => ({
      ...job,
      waitTime: formatWaitTime(job.createdAt),
    }));
  }, [queueList, formatWaitTime]);

  const { handleSelfAssign } = useQueueSelfAssign({
    setIsServiceQueueOpen,
    caseTypeIds,
  });

  return (
    <Box
      width="75%"
      display="flex"
      flex="1"
      flexDirection="column"
      overflow="hidden"
      maxHeight="95%"
      position="relative"
      backgroundColor="white"
      borderLeft="1px solid"
      borderColor="gray.200"
    >
      <Flex direction="row" width="100%" gap={4} p={4}>
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <Heading size="lg">Queue</Heading>
        </Flex>
        <Flex gap={4} alignItems="center">
          <Button
            appearance="primary"
            size="md"
            onClick={handleSelfAssign}
          >
            Quick assign
          </Button>
        </Flex>
      </Flex>
      <QueueList
        setIsServiceQueueOpen={setIsServiceQueueOpen}
        queuedJobs={jobsWithWaitTime}
        isLoading={isLoading}
        loadMoreData={loadMoreData}
        hasNextPage={hasMore}
      />
    </Box>
  );
};

export default QueueListContainer;
